import { useCallback, useEffect, useMemo, useState } from "react"

import {
  DEFAULT_SEARCH_FILTERS_DRAFT,
  type SearchFiltersDraft,
} from "@/lib/search-filter-model"

import { useSearchCountEstimate } from "./use-search-count-estimate"
import { useSearchPromptAnalysis } from "./use-search-prompt-analysis"
import {
  type SearchReviewSource,
  useSearchReviewState,
} from "./use-search-review-state"

export type SearchPreflightStatus =
  | "idle"
  | "parsing"
  | "ready"
  | "parse_error"
  | "counting"
  | "manual_counting"
  | "manual_ready"

interface UseSearchReviewOptions {
  query: string
  initialFilters?: SearchFiltersDraft
  initialSource?: SearchReviewSource
}

/**
 * Shared orchestrator for the conversational search preflight flow.
 * It exposes one stable state contract to the UI while delegating draft state,
 * prompt analysis, and count estimation to smaller hooks.
 */
export function useSearchReview({
  query,
  initialFilters,
  initialSource,
}: UseSearchReviewOptions) {
  const currentPrompt = query.trim()
  const {
    filters,
    replaceFilters,
    resetFilters,
    setFiltersFromUser,
  } = useSearchReviewState({
    initialFilters,
    initialSource,
  })
  const [manualRecoveryPrompt, setManualRecoveryPrompt] = useState("")
  const [isManualCommitted, setIsManualCommitted] = useState(false)

  const applyAiFilters = useCallback(
    (nextFilters: SearchFiltersDraft) => {
      replaceFilters(nextFilters, "ai")
      setManualRecoveryPrompt("")
      setIsManualCommitted(false)
    },
    [replaceFilters],
  )

  const resetReviewFilters = useCallback(() => {
    resetFilters()
    setManualRecoveryPrompt("")
    setIsManualCommitted(false)
  }, [resetFilters])

  const analysis = useSearchPromptAnalysis({
    query,
    onApplyFilters: applyAiFilters,
    onResetFilters: resetReviewFilters,
  })
  const isManualCommittedForCurrentPrompt =
    Boolean(currentPrompt) && manualRecoveryPrompt === currentPrompt && isManualCommitted
  const committedPrompt = isManualCommittedForCurrentPrompt ? currentPrompt : analysis.acceptedPrompt

  const count = useSearchCountEstimate({
    enabled:
      (analysis.status === "ready" && Boolean(analysis.acceptedPrompt)) || isManualCommittedForCurrentPrompt,
    filters,
    prompt: committedPrompt,
  })

  useEffect(() => {
    if (!currentPrompt || (manualRecoveryPrompt && manualRecoveryPrompt !== currentPrompt)) {
      setManualRecoveryPrompt("")
      setIsManualCommitted(false)
    }
  }, [currentPrompt, manualRecoveryPrompt])

  const startManualRecovery = useCallback(() => {
    if (!currentPrompt) return

    if (manualRecoveryPrompt !== currentPrompt) {
      replaceFilters(DEFAULT_SEARCH_FILTERS_DRAFT, "manual")
    }

    setManualRecoveryPrompt(currentPrompt)
    setIsManualCommitted(false)
  }, [currentPrompt, manualRecoveryPrompt, replaceFilters])

  const setFiltersFromManualReview = useCallback(
    (nextValue: SearchFiltersDraft) => {
      const nextFilters = setFiltersFromUser(nextValue)

      if (currentPrompt) {
        setManualRecoveryPrompt(currentPrompt)
        setIsManualCommitted(true)
      }

      return nextFilters
    },
    [currentPrompt, setFiltersFromUser],
  )

  const status = useMemo<SearchPreflightStatus>(() => {
    if (!currentPrompt) return "idle"
    if (isManualCommittedForCurrentPrompt) {
      return count.isCounting ? "manual_counting" : "manual_ready"
    }
    if (analysis.status === "parse_error") return "parse_error"
    if (analysis.status !== "ready") return "parsing"
    if (count.isCounting) return "counting"
    return "ready"
  }, [analysis.status, count.isCounting, currentPrompt, isManualCommittedForCurrentPrompt])

  return {
    acceptedPrompt: analysis.acceptedPrompt,
    attemptCount: analysis.attemptCount,
    canRunSearch: status === "ready" || status === "manual_ready",
    countError: count.countError,
    estimatedCount: count.estimatedCount,
    filters,
    isCounting: count.isCounting,
    isRetrying: analysis.isRetrying,
    isManualCommittedForCurrentPrompt,
    isManualRecoveryAvailable: analysis.status === "parse_error",
    maxAttempts: analysis.maxAttempts,
    parseError: analysis.parseError,
    resetFilters: resetReviewFilters,
    setFiltersFromManualReview,
    setFiltersFromUser,
    startManualRecovery,
    status,
  }
}

export type { SearchReviewSource } from "./use-search-review-state"
